import { BoothFeedbackCategory } from '@cibli/shared';
import { Speaker } from '@micdrop/web';
import { useCallback, useEffect, useRef, useState } from 'react';

export const FEEDBACK_AUDIO_KEYS = [
  'categories_intro',
  'language_changed',
  'category_confirm',
  'record_rules',
  'recording_active',
  'recording_ending',
  'clip_saved',
  'record_silence',
  'mic_unavailable',
  'clip_delete_confirm',
  'ready_to_send',
  'queued_offline',
  'sent_online',
  'sent_after_sync',
] as const;

export type FeedbackAudioKey = (typeof FEEDBACK_AUDIO_KEYS)[number];

const CATEGORY_AUDIO_MAP: Record<BoothFeedbackCategory, string> = {
  MICROPHONE: 'MICROPHONE',
  CAMERA: 'CAMERA',
  SOUND_OUTPUT: 'SOUND_OUTPUT',
  TOUCHSCREEN: 'TOUCHSCREEN',
  APP_BLOCKED: 'APP_BLOCKED',
  CONNECTIVITY: 'CONNECTIVITY',
  COMFORT: 'COMFORT',
  CLEANLINESS: 'CLEANLINESS',
  OTHER: 'OTHER',
};

function getStaticAudioUrl(locale: string, key: string): string {
  return `/audio/feedback/${locale}/${key}.pcm`;
}

function getCategoryAudioUrl(locale: string, category: BoothFeedbackCategory): string {
  const catKey = CATEGORY_AUDIO_MAP[category];
  return `/audio/feedback/${locale}/categories/feedback_category_confirm_${catKey}.pcm`;
}

import { getCachedAudio, setCachedAudio } from '../lib/offlineDb';

function createAudioBlob(data: ArrayBuffer): Blob {
  return new Blob([data], { type: 'audio/pcm' });
}

async function loadAudio(url: string): Promise<Blob | null> {
  // 1. Check IndexedDB cache (persists across refresh)
  const cached = await getCachedAudio(url);
  if (cached?.blob) return cached.blob;

  // 2. Fetch from network
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const arrayBuffer = await response.arrayBuffer();
    const blob = createAudioBlob(arrayBuffer);
    // Store in IndexedDB for offline use (spec §5)
    await setCachedAudio({ key: url, blob, version: 1, durationMs: 0 });
    return blob;
  } catch {
    return null;
  }
}

export function useFeedbackAssistant(initialLocale = 'fr') {
  const abortRef = useRef<AbortController | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  // Use ref for locale so play() always reads the latest value
  const localeRef = useRef(initialLocale);
  localeRef.current = initialLocale;

  const play = useCallback(
    async (messageKey: FeedbackAudioKey, category?: BoothFeedbackCategory) => {
      if (Speaker.isPlaying) Speaker.stopAudio();
      if (abortRef.current) abortRef.current.abort();

      const controller = new AbortController();
      abortRef.current = controller;
      const locale = localeRef.current;

      try {
        let url: string;
        if (messageKey === 'category_confirm' && category) {
          url = getCategoryAudioUrl(locale, category);
        } else if (messageKey === 'category_confirm') {
          url = getStaticAudioUrl(locale, 'feedback_category_confirm');
        } else {
          url = getStaticAudioUrl(locale, `feedback_${messageKey}`);
        }

        const blob = await loadAudio(url);
        if (!blob) {
          console.warn(`[FeedbackAudio] No blob for ${url}`);
          return;
        }
        // Skip stale audio queued before a stop() call
        const arrayBuffer = await blob.arrayBuffer();
        await new Promise<void>(resolve => {
          const handler = () => {
            Speaker.off('StopPlaying', handler);
            setIsPlaying(false);
            resolve();
          };
          Speaker.on('StopPlaying', handler);
          setIsPlaying(true);
          Speaker.playAudio(arrayBuffer);
        });
      } catch (error) {
        setIsPlaying(false);
        if (error instanceof Error && error.name === 'AbortError') return;
        console.error(`Failed to play feedback audio (${messageKey}):`, error);
      }
    },
    [],
  );

  const stop = useCallback(() => {
    if (Speaker.isPlaying) Speaker.stopAudio();
    setIsPlaying(false);
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (Speaker.isPlaying) Speaker.stopAudio();
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  return { play, stop, isPlaying };
}
